<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import { useScreen } from "@/composable/useResponsive";
import type { Diagnostic } from "@/types";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import MapIcon from "@/components/UI/icons/MapIcon.vue";
import ProjectAvatar from "@/components/projects/ProjectAvatar.vue";
import DiagnosticActionsDropdown from "@/components/diagnostics/DiagnosticActionsDropdown.vue";
import DiagnosticReportFileChip from "./DiagnosticReportFileChip.vue";

/**
 * En-tête de la page ouvrage : fil d'Ariane projet / ouvrage à gauche, rapport
 * précédent et menu d'options à droite. Sans fond : c'est une barre de contexte,
 * pas une carte.
 */

const props = defineProps<{
  diagnostic: Diagnostic;
}>();

const emit = defineEmits<{
  (e: "back"): void;
  (e: "open-project"): void;
  (e: "rename"): void;
  (e: "import-report"): void;
  (e: "deleted"): void;
}>();

const { t } = useTranslation();
const { screen } = useScreen();

const project = computed(() => props.diagnostic.project);

const iconSize = computed(() => (screen.value.gt.sm ? 28 : 20));
</script>

<template>
  <header class="diagnostic-header">
    <DRBtnIcon
      icon="bi-arrow-left"
      variant="flat"
      :size="34"
      class="flex-shrink-0"
      @click="emit('back')"
    />

    <!-- Fil d'Ariane : projet / ouvrage -->
    <nav class="breadcrumb-trail">
      <button
        v-if="project"
        type="button"
        class="crumb crumb-project"
        @click="emit('open-project')"
      >
        <ProjectAvatar :project="project" :size="iconSize" />
        <span class="crumb-name">{{ project.name }}</span>
      </button>

      <span v-if="project" class="crumb-separator" aria-hidden="true">/</span>

      <div class="crumb crumb-current">
        <MapIcon :size="iconSize" class="flex-shrink-0" />
        <span v-if="diagnostic.name" class="crumb-name">
          {{ diagnostic.name }}
        </span>
        <span v-else class="crumb-name text-muted">
          {{ t("report:diagnostics.noname") }}
        </span>
        <DRBtnIcon
          icon="bi-pencil"
          variant="flat"
          :size="26"
          class="edit-name"
          :title="t('rename')"
          @click="emit('rename')"
        />
      </div>
    </nav>

    <!-- Sur petit écran, le chip passe seul sur la ligne du dessous : le menu
         d'options reste donc en bout de la ligne du fil d'Ariane. -->
    <DiagnosticActionsDropdown
      class="header-menu"
      :diagnostic="diagnostic"
      :size="34"
      show-rename
      show-past-report
      :show-reports="false"
      :show-google-maps="false"
      @rename="emit('rename')"
      @import-report="emit('import-report')"
      @deleted="emit('deleted')"
    />

    <div class="header-chip">
      <DiagnosticReportFileChip
        :diagnostic="diagnostic"
        @import="emit('import-report')"
      />
    </div>
  </header>
</template>

<style lang="scss" scoped>
.diagnostic-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  flex-wrap: wrap;
  max-width: 100%;
}

.breadcrumb-trail {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.crumb {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  border: none;
  background: none;
  padding: 2px 4px;
  border-radius: 8px;
  color: var(--bs-body-color);
}

.crumb-project {
  cursor: pointer;

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }

  .crumb-name {
    font-size: 16px;
    font-weight: 500;
  }
}

.crumb-current .crumb-name {
  font-size: 20px;
  font-weight: 600;
}

.crumb-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crumb-separator {
  color: var(--bs-secondary-color);
  font-size: 18px;
  flex-shrink: 0;
}

.edit-name {
  flex-shrink: 0;
  opacity: 0.45;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
}

/* Ordre par défaut : … fil d'Ariane | chip rapport | menu */
.header-chip {
  order: 3;
  margin-left: auto;
  min-width: 0;
  max-width: 100%;
}

.header-menu {
  order: 4;
  flex-shrink: 0;
}

@media (max-width: 767.98px) {
  .diagnostic-header {
    padding: 12px;
    gap: 8px;
  }

  /* Titre plus léger, le fil d'Ariane reste complet (projet / ouvrage) */
  .crumb-project .crumb-name {
    font-size: 12px;
    max-width: 40%;
  }

  .crumb-current .crumb-name {
    font-size: 15px;
    font-weight: 500;
  }

  .crumb-separator {
    font-size: 14px;
  }

  .breadcrumb-trail {
    gap: 4px;
  }

  /* Le menu remonte en bout de première ligne, le chip prend la suivante */
  .header-menu {
    order: 3;
    margin-left: auto;
  }

  /* Pleine largeur sur sa propre ligne, sans jamais déborder :
     le chip tronque son nom de fichier plutôt que de pousser l'en-tête. */
  .header-chip {
    order: 4;
    flex: 0 0 100%;
    width: 100%;
    margin-left: 0;
  }

  .header-chip :deep(.report-chip) {
    width: 100%;
    max-width: 100%;
  }
}
</style>
